import { useState, useCallback, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, reportClientError } from "@/lib/queryClient";
import { PublicLayout } from "@/components/layout/public-layout";
import { ProgressIndicator } from "@/components/intake/progress-indicator";
import { ContactStep } from "@/components/intake/contact-step";
import { RequestDetailsStep } from "@/components/intake/request-details-step";
import { RefinementStep } from "@/components/intake/refinement-step";
import { CommercialStep } from "@/components/intake/commercial-step";
import { SuccessMessage } from "@/components/intake/success-message";
import {
  createUnavailableAIScopingApiResponse,
  isScopingUnavailable,
  normalizeAIScopingResponse,
  toAIScopingInternal,
} from "@shared/intake-ai-scoping";
import type {
  ContactStepData,
  RequestDetailsStepData,
  RefinementStepData,
  AIScopingResponse,
} from "@/lib/intake-schemas";
import { useTurnstileConfig } from "@/lib/turnstile";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

type Step = "contact" | "details" | "refinement" | "commercial" | "success";

const STEP_LABELS = ["Contact", "Request", "Refine", "Review"];

function getStepNumber(step: Step): number {
  switch (step) {
    case "contact":
      return 1;
    case "details":
      return 2;
    case "refinement":
      return 3;
    case "commercial":
      return 4;
    case "success":
      return 5;
  }
}

function getSessionId(): string {
  const key = "hms-intake-session";
  let sessionId = sessionStorage.getItem(key);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(key, sessionId);
  }
  return sessionId;
}

function draftSaveErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    if (error.message.startsWith("413:")) {
      return "Your attachments are too large. Please use smaller files (under 50MB each) and try again.";
    }
    return error.message;
  }
  return "Could not save your request. Please try again.";
}

export default function RequestFormPage() {
  const turnstile = useTurnstileConfig();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [currentStep, setCurrentStep] = useState<Step>("contact");
  const [contactData, setContactData] = useState<ContactStepData | null>(null);
  const [detailsData, setDetailsData] =
    useState<RequestDetailsStepData | null>(null);
  const [aiScoping, setAiScoping] = useState<AIScopingResponse | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [prefillReady, setPrefillReady] = useState(false);

  const sessionId = getSessionId();

  // Draft auto-save mutation
  const saveDraftMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      if (draftId) {
        return apiRequest("PUT", `/api/intake/drafts/${draftId}`, {
          ...data,
          sessionId,
        });
      }
      return apiRequest("POST", "/api/intake/drafts", {
        ...data,
        sessionId,
      });
    },
    onSuccess: async (response) => {
      const draft = await response.json();
      if (draft.id && !draftId) {
        setDraftId(draft.id);
      }
    },
  });

  const saveDraft = useCallback(
    (data: Record<string, unknown>) => {
      saveDraftMutation.mutate(data);
    },
    [saveDraftMutation],
  );

  // Routing decision mutation
  const routingMutation = useMutation({
    mutationFn: async (scopingData: AIScopingResponse) => {
      const response = await apiRequest("POST", "/api/intake/routing-decision", {
        aiScoping: toAIScopingInternal(scopingData),
      });
      return response.json();
    },
  });

  // Restore draft on mount; fall back to auth/client prefill when logged in
  useEffect(() => {
    let cancelled = false;

    const restoreDraftAndPrefill = async () => {
      if (authLoading) return;

      let restoredContact = false;

      try {
        const response = await fetch(
          `/api/intake/drafts/${sessionId}`,
          { credentials: "include" },
        );
        if (response.ok) {
          const draft = await response.json();
          if (cancelled) return;
          setDraftId(draft.id);
          if (draft.fullName && draft.email && draft.company) {
            setContactData({
              fullName: draft.fullName,
              email: draft.email,
              company: draft.company,
              websiteUrl: draft.websiteUrl || "",
              phone: draft.phone || "",
            });
            restoredContact = true;
          }
          if (draft.category && draft.requestDetails) {
            setDetailsData({
              category: draft.category,
              requestDetails: draft.requestDetails,
              loomUrl: draft.loomUrl || "",
              attachments: draft.attachments || [],
            });
          }
          if (draft.aiScopingData) {
            setAiScoping(
              normalizeAIScopingResponse(
                draft.aiScopingData,
              ) as AIScopingResponse,
            );
          } else if (draft.currentStep >= 3 && draft.category) {
            setAiScoping(
              createUnavailableAIScopingApiResponse(
                draft.category,
              ) as AIScopingResponse,
            );
          }
          // Restore to the appropriate step
          if (draft.currentStep === 2 && draft.fullName) {
            setCurrentStep("details");
          } else if (draft.currentStep >= 3 && draft.fullName) {
            setCurrentStep("commercial");
          }
        } else if (response.status !== 404) {
          reportClientError(
            new Error(`Draft restore failed: ${response.status}`),
            { feature: "intake", step: "draft_restore" },
          );
        }
      } catch (error) {
        reportClientError(error, {
          feature: "intake",
          step: "draft_restore",
        });
      }

      if (
        !cancelled &&
        !restoredContact &&
        isAuthenticated &&
        user?.userType === "client" &&
        user.clientId
      ) {
        try {
          const contextRes = await fetch("/api/portal/account/context", {
            credentials: "include",
          });
          if (contextRes.ok) {
            const context = await contextRes.json();
            if (!cancelled && context.email) {
              setContactData({
                fullName: context.fullName || "",
                email: context.email || "",
                company: context.company || "",
                websiteUrl: context.websiteUrl || "",
                phone: context.phone || "",
              });
            }
          }
        } catch (error) {
          reportClientError(error, {
            feature: "intake",
            step: "auth_prefill",
          });
        }
      }

      if (!cancelled) setPrefillReady(true);
    };

    restoreDraftAndPrefill();
    return () => {
      cancelled = true;
    };
  }, [sessionId, authLoading, isAuthenticated, user?.userType, user?.clientId]);

  // Step handlers
  const handleContactSubmit = async (
    data: ContactStepData,
    turnstileToken: string | null,
  ) => {
    if (turnstile.required && !turnstile.configured) {
      throw new Error("Verification is temporarily unavailable. Please try again later.");
    }

    if ((turnstile.required || turnstile.enabled) && !turnstileToken) {
      throw new Error("Please complete the verification step.");
    }

    const response = await apiRequest("POST", "/api/intake/drafts", {
      sessionId,
      currentStep: 2,
      fullName: data.fullName,
      email: data.email,
      company: data.company,
      websiteUrl: data.websiteUrl,
      phone: data.phone,
      turnstileToken,
    });
    const draft = await response.json();
    if (draft.id && !draftId) {
      setDraftId(draft.id);
    }
    setContactData(data);
    setCurrentStep("details");
  };

  const handleDetailsSubmit = async (
    data: RequestDetailsStepData,
    scopingResult: AIScopingResponse,
  ) => {
    setDetailsData(data);
    setAiScoping(scopingResult);

    try {
      await saveDraftMutation.mutateAsync({
        currentStep: 3,
        category: data.category,
        requestDetails: data.requestDetails,
        loomUrl: data.loomUrl,
        attachments: data.attachments,
        aiScopingData: scopingResult,
      });
    } catch (error) {
      toast({
        title: "Could not save your request",
        description: draftSaveErrorMessage(error),
        variant: "destructive",
      });
      return;
    }

    // Use routing decision to determine next step
    if (isScopingUnavailable(scopingResult)) {
      setCurrentStep("commercial");
      return;
    }

    try {
      const decision = await routingMutation.mutateAsync(scopingResult);

      if (
        decision.nextStep === "refinement" ||
        decision.route === "refinement"
      ) {
        setCurrentStep("refinement");
      } else {
        setCurrentStep("commercial");
      }
    } catch {
      // Fallback: go to commercial if routing fails
      setCurrentStep("commercial");
    }
  };

  const handleRefinementSubmit = (
    _data: RefinementStepData,
    refinedScoping: AIScopingResponse,
  ) => {
    setAiScoping(refinedScoping);
    setCurrentStep("commercial");
    saveDraft({
      currentStep: 4,
      aiScopingData: refinedScoping,
    });
  };

  const handleCommercialSubmit = () => {
    setCurrentStep("success");
    // Clear session storage on success
    sessionStorage.removeItem("hms-intake-session");
  };

  const handleBack = (toStep: Step) => {
    setCurrentStep(toStep);
  };

  return (
    <PublicLayout maxWidth="4xl">
      {currentStep !== "success" && (
        <ProgressIndicator
          currentStep={getStepNumber(currentStep)}
          steps={STEP_LABELS}
        />
      )}

      {currentStep === "contact" && prefillReady && (
        <ContactStep
          key={contactData ? "prefilled" : "empty"}
          onSubmit={handleContactSubmit}
          initialData={contactData}
          isAuthenticated={isAuthenticated}
          turnstileSiteKey={turnstile.siteKey}
          turnstileRequired={turnstile.required}
          turnstileConfigured={turnstile.configured}
        />
      )}

      {currentStep === "details" && contactData && (
        <RequestDetailsStep
          contactData={contactData}
          sessionId={sessionId}
          onSubmit={handleDetailsSubmit}
          onBack={() => handleBack("contact")}
          initialData={detailsData}
        />
      )}

      {currentStep === "refinement" &&
        contactData &&
        detailsData &&
        aiScoping && (
          <RefinementStep
            contactData={contactData}
            detailsData={detailsData}
            aiScoping={aiScoping}
            onSubmit={handleRefinementSubmit}
            onBack={() => handleBack("details")}
          />
        )}

      {currentStep === "commercial" &&
        contactData &&
        detailsData &&
        aiScoping && (
          <CommercialStep
            contactData={contactData}
            detailsData={detailsData}
            aiScoping={aiScoping}
            onSubmit={handleCommercialSubmit}
            onBack={() => handleBack("details")}
          />
        )}

      {currentStep === "success" && <SuccessMessage />}
    </PublicLayout>
  );
}
